<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useAiInsightsStore } from 'dashboard/stores/aiInsights';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import Button from 'dashboard/components-next/button/Button.vue';
import wootConstants from 'dashboard/constants/globals';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const aiInsightsStore = useAiInsightsStore();

const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => currentChat.value?.id);

const isOpen = computed(() => uiSettings.value.is_ai_insights_panel_open);
const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedAgentId = ref(null);
const pollingIntervals = ref({});
const copiedAnalysisId = ref(null);

const activeAgents = computed(() => aiInsightsStore.getActiveAgents);
const analyses = computed(() =>
  conversationId.value
    ? aiInsightsStore.getAnalysesForConversation(conversationId.value)
    : []
);
const isRunning = computed(() => aiInsightsStore.uiFlags.runningAnalysis);
const isFetching = computed(() => aiInsightsStore.uiFlags.fetchingAnalyses);

const close = () => {
  if (isSmallScreen.value) {
    updateUISettings({ is_ai_insights_panel_open: false });
  }
};

async function loadData() {
  if (!conversationId.value) return;
  await Promise.all([
    aiInsightsStore.fetchAgents(),
    aiInsightsStore.fetchAnalyses(conversationId.value),
  ]);
  if (!selectedAgentId.value && activeAgents.value.length) {
    selectedAgentId.value = activeAgents.value[0].id;
  }
  resumePollingForPending();
}

function onAnalysisUpdated(data) {
  if (!conversationId.value) return;
  if (data.conversation_id !== conversationId.value) return;
  const analysis = data.analysis;
  aiInsightsStore.upsertAnalysis(conversationId.value, analysis);
  if (analysis.status === 'completed' || analysis.status === 'failed') {
    clearInterval(pollingIntervals.value[analysis.id]);
    delete pollingIntervals.value[analysis.id];
  }
}

onMounted(() => {
  loadData();
  emitter.on('ai_insights.analysis_updated', onAnalysisUpdated);
});

watch(conversationId, (newId, oldId) => {
  if (!newId || newId === oldId) return;
  stopAllPolling();
  aiInsightsStore.fetchAnalyses(newId).then(resumePollingForPending);
});

onUnmounted(() => {
  stopAllPolling();
  emitter.off('ai_insights.analysis_updated', onAnalysisUpdated);
});

async function runAnalysis() {
  if (!selectedAgentId.value || !conversationId.value) return;
  try {
    const analysis = await aiInsightsStore.runAnalysis(
      conversationId.value,
      selectedAgentId.value
    );
    if (analysis.status === 'pending' || analysis.status === 'running') {
      startPolling(analysis.id);
    }
  } catch {
    useAlert(t('AI_INSIGHTS.PANEL.ERROR_START'));
  }
}

function startPolling(analysisId) {
  if (pollingIntervals.value[analysisId]) return;
  pollingIntervals.value[analysisId] = setInterval(async () => {
    try {
      const updated = await aiInsightsStore.pollAnalysis(
        conversationId.value,
        analysisId
      );
      if (updated.status === 'completed' || updated.status === 'failed') {
        clearInterval(pollingIntervals.value[analysisId]);
        delete pollingIntervals.value[analysisId];
      }
    } catch {
      clearInterval(pollingIntervals.value[analysisId]);
      delete pollingIntervals.value[analysisId];
    }
  }, 3000);
}

function resumePollingForPending() {
  analyses.value
    .filter(a => a.status === 'pending' || a.status === 'running')
    .forEach(a => startPolling(a.id));
}

function stopAllPolling() {
  Object.values(pollingIntervals.value).forEach(clearInterval);
  pollingIntervals.value = {};
}

const STATUS_COLORS = {
  pending: 'text-n-yellow-11',
  running: 'text-n-blue-11',
  completed: 'text-n-teal-11',
  failed: 'text-n-ruby-11',
};

function statusClass(status) {
  return STATUS_COLORS[status] || 'text-n-slate-11';
}

function formatCost(cents) {
  if (!cents) return null;
  return `$${(cents / 100).toFixed(4)}`;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString();
}

async function copyResult(analysis) {
  const text = analysis.result?.text || JSON.stringify(analysis.result, null, 2);
  await navigator.clipboard.writeText(text);
  copiedAnalysisId.value = analysis.id;
  setTimeout(() => { copiedAnalysisId.value = null; }, 2000);
}
</script>

<template>
  <div
    v-on-click-outside="[() => close(), { ignore: ['[data-ai-insights-button]'] }]"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="{
      'md:flex': isOpen,
      'md:hidden': !isOpen,
    }"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak shrink-0">
      <div class="flex items-center gap-2">
        <span class="i-ph-brain text-n-slate-11 text-lg" />
        <span class="text-heading-3 text-n-slate-12">{{ $t('AI_INSIGHTS.PANEL.TITLE') }}</span>
      </div>
      <Button
        ghost
        slate
        xs
        icon="i-ph-x"
        @click="updateUISettings({ is_ai_insights_panel_open: false })"
      />
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto px-4 py-4 flex flex-col gap-4">
      <p v-if="!conversationId" class="text-body-small text-n-slate-10">
        {{ $t('AI_INSIGHTS.PANEL.NO_CONVERSATION') }}
      </p>

      <template v-else>
        <!-- Run analysis -->
        <div class="flex flex-col gap-2">
          <p class="text-body-small font-medium text-n-slate-11">
            {{ $t('AI_INSIGHTS.PANEL.RUN_ANALYSIS') }}
          </p>
          <div v-if="activeAgents.length" class="flex gap-2">
            <select
              v-model="selectedAgentId"
              class="flex-1 text-sm px-2 py-1.5 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option v-for="agent in activeAgents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
            <Button
              size="sm"
              :label="isRunning ? $t('AI_INSIGHTS.PANEL.RUNNING') : $t('AI_INSIGHTS.PANEL.ANALYZE')"
              :is-loading="isRunning"
              :disabled="isRunning || !selectedAgentId"
              @click="runAnalysis"
            />
          </div>
          <p v-else class="text-body-small text-n-slate-10">
            {{ $t('AI_INSIGHTS.PANEL.NO_AGENTS') }}
          </p>
        </div>

        <div class="border-t border-n-weak" />

        <!-- History -->
        <div class="flex flex-col gap-3">
          <p class="text-body-small font-medium text-n-slate-11">
            {{ $t('AI_INSIGHTS.PANEL.HISTORY') }}
          </p>
          <div v-if="isFetching" class="text-body-small text-n-slate-10">
            {{ $t('AI_INSIGHTS.PANEL.LOADING') }}
          </div>
          <div v-else-if="!analyses.length" class="text-body-small text-n-slate-10">
            {{ $t('AI_INSIGHTS.PANEL.NO_ANALYSES') }}
          </div>
          <div
            v-for="analysis in analyses"
            v-else
            :key="analysis.id"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 flex flex-col gap-2"
          >
            <div class="flex justify-between items-center">
              <span class="text-body-small font-medium text-n-slate-12">
                {{ analysis.agent_name }}
              </span>
              <div class="flex items-center gap-2">
                <span :class="['text-xs capitalize font-medium', statusClass(analysis.status)]">
                  {{ analysis.status }}
                </span>
                <Button
                  v-if="analysis.status === 'completed' && analysis.result"
                  v-tooltip.top="copiedAnalysisId === analysis.id ? $t('AI_INSIGHTS.PANEL.COPIED') : $t('AI_INSIGHTS.PANEL.COPY')"
                  :icon="copiedAnalysisId === analysis.id ? 'i-ph-check' : 'i-ph-copy'"
                  ghost
                  slate
                  xs
                  @click="copyResult(analysis)"
                />
              </div>
            </div>
            <p class="text-xs text-n-slate-10">
              {{ formatDate(analysis.created_at) }}
            </p>

            <div v-if="analysis.status === 'completed' && analysis.result">
              <p v-if="analysis.result.text" class="text-body-small text-n-slate-12 whitespace-pre-wrap">
                {{ analysis.result.text }}
              </p>
              <pre v-else class="text-xs bg-n-alpha-2 rounded-lg p-2 overflow-auto max-h-60 text-n-slate-11">{{ JSON.stringify(analysis.result, null, 2) }}</pre>
            </div>
            <p v-else-if="analysis.status === 'failed'" class="text-xs text-n-ruby-11">
              {{ analysis.error_message || $t('AI_INSIGHTS.PANEL.FAILED') }}
            </p>
            <p v-else-if="['running','pending'].includes(analysis.status)" class="text-xs text-n-slate-10 italic">
              {{ $t('AI_INSIGHTS.PANEL.IN_PROGRESS') }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
